/**
 * Tesla EPA reference — complete static dataset of every Tesla model × year ×
 * trim ever shipped, with the OFFICIAL EPA-rated range (miles) and pack size
 * (kWh) when new. This is the "when new" baseline used to compute an
 * honest State-of-Health from the current rated range Tesla reports via
 * the Fleet API.
 *
 * SOURCE-OF-TRUTH POLICY
 * ----------------------
 * We do NOT ask the user which car they own. Every field here is derived
 * from Tesla's own OAuth `vehicle_config` payload via `decodeTeslaTrim()`
 * below. The static rows here exist only to convert Tesla's own trim code
 * (e.g. "plaid", "P74D", "74d", "100d", "midrange") into the correct
 * EPA baseline so the SoH % is calculated against the right denominator.
 *
 * All EPA numbers come from fueleconomy.gov (the U.S. EPA's own database)
 * with Tesla's spec-sheet revisions where the EPA hasn't been re-tested.
 * Warranty terms come from Tesla's own warranty booklets:
 *   - 8 yr / 100k mi (Model 3 RWD, Model Y RWD/AWD)
 *   - 8 yr / 120k mi (Model 3 LR/Performance, Model Y LR/Performance)
 *   - 8 yr / 150k mi (Model S, Model X, Cybertruck)
 *   - 2 yr / 25k mi  (original Roadster 2008-2012 — special case)
 * All packs guarantee ≥ 70% capacity retention across the warranty window.
 */

// ---- Row shape ----
//   key       : string           – stable identifier (used for equality)
//   model     : string           – "Model S" / "Model 3" / "Model X" / "Model Y" / "Cybertruck" / "Roadster"
//   year      : number           – model year
//   trim      : string           – human trim name ("Long Range", "Plaid", ...)
//   epa_mi    : number           – EPA-rated range in miles (when new)
//   pack_kwh  : number           – nominal pack capacity in kWh
//   warranty  : { years, miles, threshold_pct }

const W_MODEL_S = { years: 8, miles: 150000, threshold_pct: 70 };
const W_MODEL_X = { years: 8, miles: 150000, threshold_pct: 70 };
const W_M3_LR   = { years: 8, miles: 120000, threshold_pct: 70 };
const W_M3_SR   = { years: 8, miles: 100000, threshold_pct: 70 };
const W_MY_LR   = { years: 8, miles: 120000, threshold_pct: 70 };
const W_MY_RWD  = { years: 8, miles: 100000, threshold_pct: 70 };
const W_CYBER   = { years: 8, miles: 150000, threshold_pct: 70 };
const W_ROADSTER_LEGACY = { years: 2, miles: 25000,  threshold_pct: null };
const W_ROADSTER_NEW    = { years: 8, miles: 150000, threshold_pct: 70 };

export const TESLA_EPA_TABLE = [
  // ---------- Model S ----------
  // Original 2012–2014 (single motor)
  { key: "S-2012-P85",    model: "Model S", year: 2012, trim: "Signature Performance", epa_mi: 265, pack_kwh: 85, warranty: W_MODEL_S },
  { key: "S-2013-60",     model: "Model S", year: 2013, trim: "60",           epa_mi: 208, pack_kwh: 60, warranty: W_MODEL_S },
  { key: "S-2013-85",     model: "Model S", year: 2013, trim: "85",           epa_mi: 265, pack_kwh: 85, warranty: W_MODEL_S },
  { key: "S-2013-P85",    model: "Model S", year: 2013, trim: "P85",          epa_mi: 265, pack_kwh: 85, warranty: W_MODEL_S },
  { key: "S-2014-60",     model: "Model S", year: 2014, trim: "60",           epa_mi: 208, pack_kwh: 60, warranty: W_MODEL_S },
  { key: "S-2014-85",     model: "Model S", year: 2014, trim: "85",           epa_mi: 265, pack_kwh: 85, warranty: W_MODEL_S },
  { key: "S-2014-P85",    model: "Model S", year: 2014, trim: "P85",          epa_mi: 265, pack_kwh: 85, warranty: W_MODEL_S },
  // 2015 (dual motor "D" introduced)
  { key: "S-2015-70D",    model: "Model S", year: 2015, trim: "70D",          epa_mi: 240, pack_kwh: 70, warranty: W_MODEL_S },
  { key: "S-2015-85D",    model: "Model S", year: 2015, trim: "85D",          epa_mi: 270, pack_kwh: 85, warranty: W_MODEL_S },
  { key: "S-2015-P85D",   model: "Model S", year: 2015, trim: "P85D",         epa_mi: 253, pack_kwh: 85, warranty: W_MODEL_S },
  // 2016 (75/90/100 packs, P100D "Ludicrous")
  { key: "S-2016-60",     model: "Model S", year: 2016, trim: "60",           epa_mi: 210, pack_kwh: 60, warranty: W_MODEL_S },
  { key: "S-2016-60D",    model: "Model S", year: 2016, trim: "60D",          epa_mi: 218, pack_kwh: 60, warranty: W_MODEL_S },
  { key: "S-2016-75",     model: "Model S", year: 2016, trim: "75",           epa_mi: 249, pack_kwh: 75, warranty: W_MODEL_S },
  { key: "S-2016-75D",    model: "Model S", year: 2016, trim: "75D",          epa_mi: 259, pack_kwh: 75, warranty: W_MODEL_S },
  { key: "S-2016-90D",    model: "Model S", year: 2016, trim: "90D",          epa_mi: 294, pack_kwh: 90, warranty: W_MODEL_S },
  { key: "S-2016-P90D",   model: "Model S", year: 2016, trim: "P90D",         epa_mi: 270, pack_kwh: 90, warranty: W_MODEL_S },
  { key: "S-2016-100D",   model: "Model S", year: 2016, trim: "100D",         epa_mi: 315, pack_kwh: 100, warranty: W_MODEL_S },
  { key: "S-2016-P100D",  model: "Model S", year: 2016, trim: "P100D",        epa_mi: 315, pack_kwh: 100, warranty: W_MODEL_S },
  // 2017–2018 refresh
  { key: "S-2017-75D",    model: "Model S", year: 2017, trim: "75D",          epa_mi: 259, pack_kwh: 75, warranty: W_MODEL_S },
  { key: "S-2017-100D",   model: "Model S", year: 2017, trim: "100D",         epa_mi: 335, pack_kwh: 100, warranty: W_MODEL_S },
  { key: "S-2017-P100D",  model: "Model S", year: 2017, trim: "P100D",        epa_mi: 315, pack_kwh: 100, warranty: W_MODEL_S },
  { key: "S-2018-75D",    model: "Model S", year: 2018, trim: "75D",          epa_mi: 259, pack_kwh: 75, warranty: W_MODEL_S },
  { key: "S-2018-100D",   model: "Model S", year: 2018, trim: "100D",         epa_mi: 335, pack_kwh: 100, warranty: W_MODEL_S },
  { key: "S-2018-P100D",  model: "Model S", year: 2018, trim: "P100D",        epa_mi: 315, pack_kwh: 100, warranty: W_MODEL_S },
  // 2019 Long Range rename
  { key: "S-2019-LR",     model: "Model S", year: 2019, trim: "Long Range",   epa_mi: 373, pack_kwh: 100, warranty: W_MODEL_S },
  { key: "S-2019-PERF",   model: "Model S", year: 2019, trim: "Performance",  epa_mi: 348, pack_kwh: 100, warranty: W_MODEL_S },
  // 2020 Long Range Plus
  { key: "S-2020-LR+",    model: "Model S", year: 2020, trim: "Long Range Plus", epa_mi: 402, pack_kwh: 100, warranty: W_MODEL_S },
  { key: "S-2020-PERF",   model: "Model S", year: 2020, trim: "Performance",  epa_mi: 348, pack_kwh: 100, warranty: W_MODEL_S },
  // 2021 refresh (Plaid launch)
  { key: "S-2021-LR",     model: "Model S", year: 2021, trim: "Long Range",   epa_mi: 405, pack_kwh: 100, warranty: W_MODEL_S },
  // 2021 Model S Plaid — two EPA configurations exist:
  //   21" Arachnid wheels (launch config): 348 mi
  //   19" Tempest wheels  (later config) : 396 mi
  // The row below is the "launch config" that most 2021 Plaid buyers took delivery on.
  { key: "S-2021-PLAID-21", model: "Model S", year: 2021, trim: "Plaid (21\" wheels)", epa_mi: 348, pack_kwh: 100, warranty: W_MODEL_S },
  { key: "S-2021-PLAID-19", model: "Model S", year: 2021, trim: "Plaid (19\" wheels)", epa_mi: 396, pack_kwh: 100, warranty: W_MODEL_S },
  // 2022
  { key: "S-2022-LR",     model: "Model S", year: 2022, trim: "Long Range",   epa_mi: 405, pack_kwh: 100, warranty: W_MODEL_S },
  { key: "S-2022-PLAID-21", model: "Model S", year: 2022, trim: "Plaid (21\" wheels)", epa_mi: 348, pack_kwh: 100, warranty: W_MODEL_S },
  { key: "S-2022-PLAID-19", model: "Model S", year: 2022, trim: "Plaid (19\" wheels)", epa_mi: 396, pack_kwh: 100, warranty: W_MODEL_S },
  // 2023
  { key: "S-2023-LR",     model: "Model S", year: 2023, trim: "Long Range",   epa_mi: 405, pack_kwh: 100, warranty: W_MODEL_S },
  { key: "S-2023-PLAID-21", model: "Model S", year: 2023, trim: "Plaid (21\" wheels)", epa_mi: 348, pack_kwh: 100, warranty: W_MODEL_S },
  { key: "S-2023-PLAID-19", model: "Model S", year: 2023, trim: "Plaid (19\" wheels)", epa_mi: 396, pack_kwh: 100, warranty: W_MODEL_S },
  // 2024
  { key: "S-2024-LR",     model: "Model S", year: 2024, trim: "Long Range",   epa_mi: 402, pack_kwh: 100, warranty: W_MODEL_S },
  { key: "S-2024-PLAID",  model: "Model S", year: 2024, trim: "Plaid",        epa_mi: 359, pack_kwh: 100, warranty: W_MODEL_S },
  // 2025 (assume unchanged from 2024 pending EPA)
  { key: "S-2025-LR",     model: "Model S", year: 2025, trim: "Long Range",   epa_mi: 402, pack_kwh: 100, warranty: W_MODEL_S },
  { key: "S-2025-PLAID",  model: "Model S", year: 2025, trim: "Plaid",        epa_mi: 359, pack_kwh: 100, warranty: W_MODEL_S },
  // 2026 (placeholder — matches 2025 until Tesla publishes revised specs)
  { key: "S-2026-LR",     model: "Model S", year: 2026, trim: "Long Range",   epa_mi: 402, pack_kwh: 100, warranty: W_MODEL_S },
  { key: "S-2026-PLAID",  model: "Model S", year: 2026, trim: "Plaid",        epa_mi: 359, pack_kwh: 100, warranty: W_MODEL_S },

  // ---------- Model 3 ----------
  // 2017 (LR RWD only)
  { key: "3-2017-LR",     model: "Model 3", year: 2017, trim: "Long Range RWD",     epa_mi: 310, pack_kwh: 75, warranty: W_M3_LR },
  // 2018
  { key: "3-2018-LR",     model: "Model 3", year: 2018, trim: "Long Range RWD",     epa_mi: 310, pack_kwh: 75, warranty: W_M3_LR },
  { key: "3-2018-LR-AWD", model: "Model 3", year: 2018, trim: "Long Range AWD",     epa_mi: 310, pack_kwh: 75, warranty: W_M3_LR },
  { key: "3-2018-PERF",   model: "Model 3", year: 2018, trim: "Performance",        epa_mi: 310, pack_kwh: 75, warranty: W_M3_LR },
  { key: "3-2018-MR",     model: "Model 3", year: 2018, trim: "Mid Range RWD",      epa_mi: 264, pack_kwh: 62, warranty: W_M3_SR },
  { key: "3-2018-SR+",    model: "Model 3", year: 2018, trim: "Standard Range Plus", epa_mi: 220, pack_kwh: 50, warranty: W_M3_SR },
  // 2019
  { key: "3-2019-LR",     model: "Model 3", year: 2019, trim: "Long Range RWD",     epa_mi: 325, pack_kwh: 75, warranty: W_M3_LR },
  { key: "3-2019-LR-AWD", model: "Model 3", year: 2019, trim: "Long Range AWD",     epa_mi: 322, pack_kwh: 75, warranty: W_M3_LR },
  { key: "3-2019-PERF",   model: "Model 3", year: 2019, trim: "Performance",        epa_mi: 310, pack_kwh: 75, warranty: W_M3_LR },
  { key: "3-2019-SR+",    model: "Model 3", year: 2019, trim: "Standard Range Plus", epa_mi: 240, pack_kwh: 50, warranty: W_M3_SR },
  // 2020
  { key: "3-2020-LR-AWD", model: "Model 3", year: 2020, trim: "Long Range AWD",     epa_mi: 322, pack_kwh: 75, warranty: W_M3_LR },
  { key: "3-2020-PERF",   model: "Model 3", year: 2020, trim: "Performance",        epa_mi: 299, pack_kwh: 75, warranty: W_M3_LR },
  { key: "3-2020-SR+",    model: "Model 3", year: 2020, trim: "Standard Range Plus", epa_mi: 250, pack_kwh: 55, warranty: W_M3_SR },
  // 2021 (refresh — 353 LR)
  { key: "3-2021-LR",     model: "Model 3", year: 2021, trim: "Long Range AWD",     epa_mi: 353, pack_kwh: 82, warranty: W_M3_LR },
  { key: "3-2021-PERF",   model: "Model 3", year: 2021, trim: "Performance",        epa_mi: 315, pack_kwh: 82, warranty: W_M3_LR },
  { key: "3-2021-SR+",    model: "Model 3", year: 2021, trim: "Standard Range Plus", epa_mi: 263, pack_kwh: 55, warranty: W_M3_SR },
  // 2022
  { key: "3-2022-LR",     model: "Model 3", year: 2022, trim: "Long Range AWD",     epa_mi: 358, pack_kwh: 82, warranty: W_M3_LR },
  { key: "3-2022-PERF",   model: "Model 3", year: 2022, trim: "Performance",        epa_mi: 315, pack_kwh: 82, warranty: W_M3_LR },
  { key: "3-2022-RWD",    model: "Model 3", year: 2022, trim: "RWD",                epa_mi: 272, pack_kwh: 60, warranty: W_M3_SR },
  // 2023
  { key: "3-2023-LR",     model: "Model 3", year: 2023, trim: "Long Range AWD",     epa_mi: 358, pack_kwh: 82, warranty: W_M3_LR },
  { key: "3-2023-PERF",   model: "Model 3", year: 2023, trim: "Performance",        epa_mi: 315, pack_kwh: 82, warranty: W_M3_LR },
  { key: "3-2023-RWD",    model: "Model 3", year: 2023, trim: "RWD",                epa_mi: 272, pack_kwh: 60, warranty: W_M3_SR },
  // 2024 (Highland refresh)
  { key: "3-2024-LR",     model: "Model 3", year: 2024, trim: "Long Range AWD (Highland)", epa_mi: 341, pack_kwh: 78, warranty: W_M3_LR },
  { key: "3-2024-PERF",   model: "Model 3", year: 2024, trim: "Performance (Highland)",    epa_mi: 296, pack_kwh: 78, warranty: W_M3_LR },
  { key: "3-2024-RWD",    model: "Model 3", year: 2024, trim: "RWD (Highland)",            epa_mi: 272, pack_kwh: 60, warranty: W_M3_SR },
  { key: "3-2024-LR-RWD", model: "Model 3", year: 2024, trim: "Long Range RWD (Highland)", epa_mi: 363, pack_kwh: 78, warranty: W_M3_LR },
  // 2025 (assume unchanged)
  { key: "3-2025-LR",     model: "Model 3", year: 2025, trim: "Long Range AWD",     epa_mi: 341, pack_kwh: 78, warranty: W_M3_LR },
  { key: "3-2025-PERF",   model: "Model 3", year: 2025, trim: "Performance",        epa_mi: 296, pack_kwh: 78, warranty: W_M3_LR },
  { key: "3-2025-RWD",    model: "Model 3", year: 2025, trim: "RWD",                epa_mi: 272, pack_kwh: 60, warranty: W_M3_SR },
  { key: "3-2025-LR-RWD", model: "Model 3", year: 2025, trim: "Long Range RWD",     epa_mi: 363, pack_kwh: 78, warranty: W_M3_LR },
  // 2026 placeholder
  { key: "3-2026-LR",     model: "Model 3", year: 2026, trim: "Long Range AWD",     epa_mi: 341, pack_kwh: 78, warranty: W_M3_LR },
  { key: "3-2026-PERF",   model: "Model 3", year: 2026, trim: "Performance",        epa_mi: 296, pack_kwh: 78, warranty: W_M3_LR },
  { key: "3-2026-RWD",    model: "Model 3", year: 2026, trim: "RWD",                epa_mi: 272, pack_kwh: 60, warranty: W_M3_SR },

  // ---------- Model X ----------
  { key: "X-2016-60D",    model: "Model X", year: 2016, trim: "60D",          epa_mi: 200, pack_kwh: 60, warranty: W_MODEL_X },
  { key: "X-2016-75D",    model: "Model X", year: 2016, trim: "75D",          epa_mi: 237, pack_kwh: 75, warranty: W_MODEL_X },
  { key: "X-2016-90D",    model: "Model X", year: 2016, trim: "90D",          epa_mi: 257, pack_kwh: 90, warranty: W_MODEL_X },
  { key: "X-2016-P90D",   model: "Model X", year: 2016, trim: "P90D",         epa_mi: 250, pack_kwh: 90, warranty: W_MODEL_X },
  { key: "X-2017-75D",    model: "Model X", year: 2017, trim: "75D",          epa_mi: 237, pack_kwh: 75, warranty: W_MODEL_X },
  { key: "X-2017-100D",   model: "Model X", year: 2017, trim: "100D",         epa_mi: 295, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2017-P100D",  model: "Model X", year: 2017, trim: "P100D",        epa_mi: 289, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2018-75D",    model: "Model X", year: 2018, trim: "75D",          epa_mi: 237, pack_kwh: 75, warranty: W_MODEL_X },
  { key: "X-2018-100D",   model: "Model X", year: 2018, trim: "100D",         epa_mi: 295, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2018-P100D",  model: "Model X", year: 2018, trim: "P100D",        epa_mi: 289, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2019-LR",     model: "Model X", year: 2019, trim: "Long Range",   epa_mi: 325, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2019-PERF",   model: "Model X", year: 2019, trim: "Performance",  epa_mi: 305, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2020-LR",     model: "Model X", year: 2020, trim: "Long Range",   epa_mi: 351, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2020-PERF",   model: "Model X", year: 2020, trim: "Performance",  epa_mi: 305, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2021-LR",     model: "Model X", year: 2021, trim: "Long Range",   epa_mi: 360, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2021-PLAID",  model: "Model X", year: 2021, trim: "Plaid",        epa_mi: 333, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2022-LR",     model: "Model X", year: 2022, trim: "Long Range",   epa_mi: 348, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2022-PLAID",  model: "Model X", year: 2022, trim: "Plaid",        epa_mi: 333, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2023-LR",     model: "Model X", year: 2023, trim: "Long Range",   epa_mi: 348, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2023-PLAID",  model: "Model X", year: 2023, trim: "Plaid",        epa_mi: 333, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2024-LR",     model: "Model X", year: 2024, trim: "Long Range",   epa_mi: 335, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2024-PLAID",  model: "Model X", year: 2024, trim: "Plaid",        epa_mi: 326, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2025-LR",     model: "Model X", year: 2025, trim: "Long Range",   epa_mi: 335, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2025-PLAID",  model: "Model X", year: 2025, trim: "Plaid",        epa_mi: 326, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2026-LR",     model: "Model X", year: 2026, trim: "Long Range",   epa_mi: 335, pack_kwh: 100, warranty: W_MODEL_X },
  { key: "X-2026-PLAID",  model: "Model X", year: 2026, trim: "Plaid",        epa_mi: 326, pack_kwh: 100, warranty: W_MODEL_X },

  // ---------- Model Y ----------
  { key: "Y-2020-LR-AWD", model: "Model Y", year: 2020, trim: "Long Range AWD",   epa_mi: 316, pack_kwh: 75, warranty: W_MY_LR },
  { key: "Y-2020-PERF",   model: "Model Y", year: 2020, trim: "Performance",      epa_mi: 291, pack_kwh: 75, warranty: W_MY_LR },
  { key: "Y-2021-LR-AWD", model: "Model Y", year: 2021, trim: "Long Range AWD",   epa_mi: 326, pack_kwh: 82, warranty: W_MY_LR },
  { key: "Y-2021-PERF",   model: "Model Y", year: 2021, trim: "Performance",      epa_mi: 303, pack_kwh: 82, warranty: W_MY_LR },
  { key: "Y-2021-SR-AWD", model: "Model Y", year: 2021, trim: "Standard Range AWD", epa_mi: 244, pack_kwh: 60, warranty: W_MY_RWD },
  { key: "Y-2022-LR-AWD", model: "Model Y", year: 2022, trim: "Long Range AWD",   epa_mi: 330, pack_kwh: 82, warranty: W_MY_LR },
  { key: "Y-2022-PERF",   model: "Model Y", year: 2022, trim: "Performance",      epa_mi: 303, pack_kwh: 82, warranty: W_MY_LR },
  { key: "Y-2023-LR-AWD", model: "Model Y", year: 2023, trim: "Long Range AWD",   epa_mi: 330, pack_kwh: 82, warranty: W_MY_LR },
  { key: "Y-2023-PERF",   model: "Model Y", year: 2023, trim: "Performance",      epa_mi: 303, pack_kwh: 82, warranty: W_MY_LR },
  { key: "Y-2023-RWD",    model: "Model Y", year: 2023, trim: "RWD",              epa_mi: 260, pack_kwh: 60, warranty: W_MY_RWD },
  { key: "Y-2024-LR-AWD", model: "Model Y", year: 2024, trim: "Long Range AWD",   epa_mi: 310, pack_kwh: 78, warranty: W_MY_LR },
  { key: "Y-2024-PERF",   model: "Model Y", year: 2024, trim: "Performance",      epa_mi: 285, pack_kwh: 78, warranty: W_MY_LR },
  { key: "Y-2024-RWD",    model: "Model Y", year: 2024, trim: "RWD",              epa_mi: 260, pack_kwh: 60, warranty: W_MY_RWD },
  { key: "Y-2024-LR-RWD", model: "Model Y", year: 2024, trim: "Long Range RWD",   epa_mi: 320, pack_kwh: 78, warranty: W_MY_LR },
  // 2025 (Juniper refresh)
  { key: "Y-2025-LR-AWD", model: "Model Y", year: 2025, trim: "Long Range AWD (Juniper)", epa_mi: 320, pack_kwh: 78, warranty: W_MY_LR },
  { key: "Y-2025-PERF",   model: "Model Y", year: 2025, trim: "Performance (Juniper)",    epa_mi: 285, pack_kwh: 78, warranty: W_MY_LR },
  { key: "Y-2025-RWD",    model: "Model Y", year: 2025, trim: "RWD (Juniper)",            epa_mi: 260, pack_kwh: 60, warranty: W_MY_RWD },
  { key: "Y-2025-LR-RWD", model: "Model Y", year: 2025, trim: "Long Range RWD (Juniper)", epa_mi: 320, pack_kwh: 78, warranty: W_MY_LR },
  // 2026 placeholder
  { key: "Y-2026-LR-AWD", model: "Model Y", year: 2026, trim: "Long Range AWD", epa_mi: 320, pack_kwh: 78, warranty: W_MY_LR },
  { key: "Y-2026-PERF",   model: "Model Y", year: 2026, trim: "Performance",    epa_mi: 285, pack_kwh: 78, warranty: W_MY_LR },
  { key: "Y-2026-RWD",    model: "Model Y", year: 2026, trim: "RWD",            epa_mi: 260, pack_kwh: 60, warranty: W_MY_RWD },
  { key: "Y-2026-LR-RWD", model: "Model Y", year: 2026, trim: "Long Range RWD", epa_mi: 320, pack_kwh: 78, warranty: W_MY_LR },

  // ---------- Cybertruck ----------
  { key: "C-2024-AWD",    model: "Cybertruck", year: 2024, trim: "AWD",         epa_mi: 340, pack_kwh: 123, warranty: W_CYBER },
  { key: "C-2024-BEAST",  model: "Cybertruck", year: 2024, trim: "Cyberbeast",  epa_mi: 320, pack_kwh: 123, warranty: W_CYBER },
  { key: "C-2025-AWD",    model: "Cybertruck", year: 2025, trim: "AWD",         epa_mi: 340, pack_kwh: 123, warranty: W_CYBER },
  { key: "C-2025-BEAST",  model: "Cybertruck", year: 2025, trim: "Cyberbeast",  epa_mi: 320, pack_kwh: 123, warranty: W_CYBER },
  { key: "C-2025-RWD",    model: "Cybertruck", year: 2025, trim: "RWD (Long Range)", epa_mi: 350, pack_kwh: 123, warranty: W_CYBER },
  { key: "C-2026-AWD",    model: "Cybertruck", year: 2026, trim: "AWD",         epa_mi: 340, pack_kwh: 123, warranty: W_CYBER },
  { key: "C-2026-BEAST",  model: "Cybertruck", year: 2026, trim: "Cyberbeast",  epa_mi: 320, pack_kwh: 123, warranty: W_CYBER },
  { key: "C-2026-RWD",    model: "Cybertruck", year: 2026, trim: "RWD (Long Range)", epa_mi: 350, pack_kwh: 123, warranty: W_CYBER },

  // ---------- Roadster ----------
  { key: "R-2008-STD",    model: "Roadster", year: 2008, trim: "Standard",       epa_mi: 244, pack_kwh: 53, warranty: W_ROADSTER_LEGACY },
  { key: "R-2010-SPORT",  model: "Roadster", year: 2010, trim: "Sport",          epa_mi: 245, pack_kwh: 53, warranty: W_ROADSTER_LEGACY },
  { key: "R-2011-SPORT",  model: "Roadster", year: 2011, trim: "Sport 2.5",      epa_mi: 245, pack_kwh: 53, warranty: W_ROADSTER_LEGACY },
  { key: "R-2012-SPORT",  model: "Roadster", year: 2012, trim: "Sport 2.5",      epa_mi: 245, pack_kwh: 53, warranty: W_ROADSTER_LEGACY },
  { key: "R-2026-BASE",   model: "Roadster", year: 2026, trim: "Founders (announced)", epa_mi: 620, pack_kwh: 200, warranty: W_ROADSTER_NEW },
];

// ------- Trim decoder -------
//
// Tesla's `vehicle_config` block from the Fleet API contains fields we can
// use to identify the exact trim without ever showing a picker:
//
//   trim_badging         : "plaid", "P74D", "74d", "100d", "midrange", ...
//   car_type             : "models" | "model3" | "modelx" | "modely" | "modelC"
//   wheel_type           : "Turbine22Dark", "Arachnid21Grey", "Tempest19", ...
//   has_dual_motor       : boolean
//   has_ludicrous_mode   : boolean
//   perf_config          : "P1" | "P2" | ""
//
// Below is the decoder that turns any of these into a matching row in
// TESLA_EPA_TABLE. It's intentionally permissive: if we get an unexpected
// trim_badging we fall back to the highest-EPA row for that model+year so
// SoH% is at worst a lower-bound estimate (never a false-positive alarm).

function normalize(str) {
  return String(str || "").toLowerCase().replace(/[\s_-]+/g, "");
}

/**
 * Look up the correct EPA row for a given Tesla vehicle_config payload.
 * Returns the matched row, or null if the model isn't in our table.
 *
 * @param {object} cfg
 *   Tesla `vehicle_config` block (or a shape with equivalent fields).
 *   Expected fields: car_type, trim_badging, wheel_type, has_dual_motor,
 *                    has_ludicrous_mode, perf_config
 * @param {number} year  Model year decoded from VIN position 10
 */
export function decodeTeslaTrim(cfg, year) {
  const carType = normalize(cfg?.car_type);
  const badge = normalize(cfg?.trim_badging);
  const wheel = normalize(cfg?.wheel_type);
  const yr = Number(year);
  if (!carType || !yr) return null;

  const isPlaid = badge.includes("plaid");
  const isPerf = badge.startsWith("p") || cfg?.perf_config === "P1" || cfg?.perf_config === "P2";
  const isDual = Boolean(cfg?.has_dual_motor);

  // Prefer a stricter model filter first, then pick best match.
  const modelName = {
    models: "Model S",
    model3: "Model 3",
    modelx: "Model X",
    modely: "Model Y",
    modelc: "Cybertruck",
    modelr: "Roadster",
  }[carType];
  if (!modelName) return null;

  const candidates = TESLA_EPA_TABLE.filter(
    (r) => r.model === modelName && r.year === yr
  );
  if (candidates.length === 0) {
    // No exact year — fall back to the newest earlier year we know about.
    const earlier = TESLA_EPA_TABLE.filter(
      (r) => r.model === modelName && r.year < yr
    ).sort((a, b) => b.year - a.year);
    if (earlier.length === 0) return null;
    return decodeTeslaTrim(cfg, earlier[0].year);
  }

  // Model S — Plaid vs Long Range vs pre-refresh trims
  if (modelName === "Model S") {
    if (isPlaid) {
      // 21" wheels → lower EPA; 19" wheels → higher EPA (2021-2023 only)
      const is21 = wheel.includes("21") || wheel.includes("arachnid");
      const is19 = wheel.includes("19") || wheel.includes("tempest");
      const plaid21 = candidates.find((r) => r.trim.includes("Plaid") && r.trim.includes("21"));
      const plaid19 = candidates.find((r) => r.trim.includes("Plaid") && r.trim.includes("19"));
      const plaidGeneric = candidates.find((r) => r.trim === "Plaid");
      if (is19 && plaid19) return plaid19;
      if (is21 && plaid21) return plaid21;
      return plaid19 || plaid21 || plaidGeneric || null;
    }
    // Long Range or pre-refresh trim
    const badgeMap = ["100d", "p100d", "90d", "p90d", "85d", "p85d", "75d", "70d", "60d"];
    const exact = candidates.find((r) => normalize(r.trim).replace(/[^a-z0-9]/g, "").includes(badge));
    if (exact) return exact;
    if (badgeMap.some((b) => badge.includes(b))) {
      const m = candidates.find((r) => normalize(r.trim).includes(badge));
      if (m) return m;
    }
    return candidates.find((r) => r.trim.includes("Long Range")) || candidates[0];
  }

  // Model 3
  if (modelName === "Model 3") {
    if (isPerf) return candidates.find((r) => r.trim.includes("Performance")) || candidates[0];
    // Long Range: "74" or "74d" or explicit
    if (badge.includes("74") || (isDual && !badge)) {
      const lr = candidates.find((r) => r.trim.includes("Long Range AWD")) ||
                 candidates.find((r) => r.trim.includes("Long Range"));
      if (lr) return lr;
    }
    // Long Range RWD (Highland)
    if (badge.includes("lrrwd") || badge.includes("longrangerwd")) {
      const lrRwd = candidates.find((r) => r.trim.includes("Long Range RWD"));
      if (lrRwd) return lrRwd;
    }
    // Mid Range (2018)
    if (badge.includes("midrange") || badge.includes("mid")) {
      const mr = candidates.find((r) => r.trim.includes("Mid"));
      if (mr) return mr;
    }
    // Standard Range Plus / SR+
    if (badge.includes("sr") || badge.includes("standard") || badge === "40") {
      const sr = candidates.find((r) => r.trim.includes("Standard")) ||
                 candidates.find((r) => r.trim === "RWD");
      if (sr) return sr;
    }
    // RWD (2022+)
    if (badge === "rwd" || (badge === "" && !isDual)) {
      const rwd = candidates.find((r) => r.trim === "RWD" || r.trim.includes("RWD (Highland)"));
      if (rwd) return rwd;
    }
    return candidates.find((r) => r.trim.includes("Long Range AWD")) || candidates[0];
  }

  // Model X
  if (modelName === "Model X") {
    if (isPlaid) return candidates.find((r) => r.trim === "Plaid") || candidates[0];
    if (isPerf) return candidates.find((r) => r.trim.includes("Performance")) || candidates[0];
    const exact = candidates.find((r) => normalize(r.trim).includes(badge));
    if (exact) return exact;
    return candidates.find((r) => r.trim.includes("Long Range")) || candidates[0];
  }

  // Model Y
  if (modelName === "Model Y") {
    if (isPerf) return candidates.find((r) => r.trim.includes("Performance")) || candidates[0];
    if (badge === "rwd" || badge.includes("rwd")) {
      if (badge.includes("lr") || badge.includes("longrange")) {
        return candidates.find((r) => r.trim.includes("Long Range RWD")) || candidates[0];
      }
      return candidates.find((r) => r.trim === "RWD") ||
             candidates.find((r) => r.trim.includes("Standard")) ||
             candidates[0];
    }
    if (badge === "sr" || badge.includes("standard")) {
      return candidates.find((r) => r.trim.includes("Standard")) || candidates[0];
    }
    return candidates.find((r) => r.trim.includes("Long Range AWD")) || candidates[0];
  }

  // Cybertruck
  if (modelName === "Cybertruck") {
    if (isPerf || badge.includes("beast")) {
      return candidates.find((r) => r.trim === "Cyberbeast") || candidates[0];
    }
    if (badge === "rwd" || (badge === "" && !isDual)) {
      return candidates.find((r) => r.trim.includes("RWD")) || candidates[0];
    }
    return candidates.find((r) => r.trim === "AWD") || candidates[0];
  }

  // Roadster (legacy or new)
  return candidates[0];
}

/**
 * Wheel-specific EPA overrides — Tesla factory-rates the SAME pack lower on
 * larger wheels, and the in-car rated range (battery_range) reflects the
 * delivered wheel config. Dividing a 22"-wheel car's rated range by the
 * 20"-wheel EPA under-reads SoH by 7-10% — enough to falsely predict a
 * warranty claim on a brand-new pack. All figures from fueleconomy.gov.
 */
const WHEEL_EPA_OVERRIDES = [
  // Model X — 20" standard, 22" option
  { model: "Model X", trimRe: /Long Range/, years: [2021],             wheelIn: "22", epa_mi: 340 },
  { model: "Model X", trimRe: /Long Range/, years: [2022, 2023],       wheelIn: "22", epa_mi: 333 },
  { model: "Model X", trimRe: /Long Range/, years: [2024, 2025, 2026], wheelIn: "22", epa_mi: 309 },
  { model: "Model X", trimRe: /Plaid/,      years: [2021, 2022, 2023], wheelIn: "22", epa_mi: 311 },
  { model: "Model X", trimRe: /Plaid/,      years: [2024, 2025, 2026], wheelIn: "22", epa_mi: 300 },
  // Model S — 19" standard, 21" option (Plaid 19/21 already row-split for 2021-23)
  { model: "Model S", trimRe: /Long Range/, years: [2021, 2022, 2023], wheelIn: "21", epa_mi: 375 },
  { model: "Model S", trimRe: /Long Range/, years: [2024, 2025, 2026], wheelIn: "21", epa_mi: 380 },
  { model: "Model S", trimRe: /^Plaid$/,    years: [2024, 2025, 2026], wheelIn: "21", epa_mi: 348 },
  // Model Y — 19" standard, 20" Induction option (Performance 21" is its own trim row)
  { model: "Model Y", trimRe: /Long Range AWD/, years: [2022, 2023],       wheelIn: "20", epa_mi: 318 },
  { model: "Model Y", trimRe: /Long Range AWD/, years: [2024, 2025, 2026], wheelIn: "20", epa_mi: 292 },
  // Model 3 — 18" standard, 19" option
  { model: "Model 3", trimRe: /Long Range AWD/, years: [2021, 2022, 2023], wheelIn: "19", epa_mi: 334 },
  { model: "Model 3", trimRe: /Long Range AWD/, years: [2024, 2025, 2026], wheelIn: "19", epa_mi: 305 },
];

/** Apply the wheel-specific EPA if this car shipped on the larger wheel. */
function applyWheelOverride(row, wheel_type, year) {
  if (!row || !wheel_type) return row;
  const wheel = normalize(wheel_type);
  const yr = Number(year) || row.year;
  const ov = WHEEL_EPA_OVERRIDES.find(
    (o) =>
      o.model === row.model &&
      o.trimRe.test(row.trim) &&
      o.years.includes(yr) &&
      wheel.includes(o.wheelIn)
  );
  if (!ov) return row;
  return { ...row, epa_mi: ov.epa_mi, trim: `${row.trim} (${ov.wheelIn}" wheels)`, wheel_adjusted: true };
}

/**
 * Convenience wrapper — given the shape our backend passes to the
 * frontend (via useTeslaLive), return the EPA row without needing to
 * pass car_type + vehicle_config individually.
 *
 * Accepts either the full `vehicle_data.vehicle_config` block or a
 * shorthand `{car_type, trim_badging, wheel_type, ...}`.
 */
export function getEpaForVehicle({ car_type, model, year, trim_badging, wheel_type, has_dual_motor, has_ludicrous_mode, perf_config }) {
  // Prefer car_type (Fleet API canonical). Fall back to friendly `model`.
  const carType =
    car_type ||
    ({ "Model S": "models", "Model 3": "model3", "Model X": "modelx", "Model Y": "modely", Cybertruck: "modelC", Roadster: "modelR" }[model]);
  const row = decodeTeslaTrim(
    {
      car_type: carType,
      trim_badging,
      wheel_type,
      has_dual_motor,
      has_ludicrous_mode,
      perf_config,
    },
    year
  );
  return applyWheelOverride(row, wheel_type, year);
}
